import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import { useSortOptions } from '@/hooks/useSortOptions';

import { withRoles } from '@/utils/withRoles';

import Filter, {
  FilterAndSortButtonProps,
  FilterCheckboxSectionProps,
  FilterDesktop,
  FilterMobile,
} from '@/components/Filter';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import { FilterLayoutProps } from '@/components/Filter/types';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';
import TeamMemberCard from '@/components/PlanCards/TeamMemberCard';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';

import { processReportTeamMembers } from '@/services/modelHelper';

import { LicenseeLocationTrigger } from '../Plans/Tab/LicenseeLocationTrigger';
import { useReportsTeamMembersFocus } from '../state';
import ToggleButtons from '../ToggleButtons';

import { getUsersSummaryReportOptions } from '@/_generated/api-clients/xp';
import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { filtersToFilterInValues } from '@/atoms/data-view/helpers';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import { TeamMemberVMFactory } from '@/viewmodels';
import { TeamMemberVM } from '@/viewmodels/types';

const sourceDataAtom = sourceDataFamily(
  DataViewAtomFamilyKey.REPORTS_TEAM_MEMBERS,
);

const ReportsTeamMembersTab = () => {
  const { t } = useTranslation();

  const { data: auth } = useAuth();
  const isOperatorConsultantUser = auth?.isOperatorConsultant ?? false;
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const isLicenseeUser = auth?.isLicensee ?? false;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const userLicenseeLocations = auth?.licenseeLocations ?? [];
  const sortOptions = useSortOptions({ reportsTeamMembersTab: true });

  const DATAVIEW_KEY = DataViewAtomFamilyKey.REPORTS_TEAM_MEMBERS;

  const [
    {
      display: { items, total, showing },
      filters,
      sort,
    },
    {
      resetAllCheckboxFilters,
      toggleCheckboxFilter,
      resetSearch,
      resetFilters,
      setSearch,
      setSort,
      showMore,
    },
  ] = useDataView<TeamMemberVM>(DATAVIEW_KEY);

  const currentFilters = filtersToFilterInValues(filters);

  const [isLicenseeMode, setIsLicenseeMode] =
    useReportsTeamMembersFocus('licenseeModeToggle');

  const locations: string[] = isLicenseeUser
    ? [activeLicenseeLocation?.number ?? '']
    : locationsWithAtLeastTrainer;

  const planLocations =
    userIsLicenseeStaff || isLicenseeUser
      ? activeLicenseeLocation?.number
        ? [activeLicenseeLocation.number]
        : []
      : isLicenseeMode
        ? userLicenseeLocations.reduce<string[]>(
            (acc, location) =>
              location.number ? [...acc, location.number] : acc,
            [],
          )
        : locationsWithAtLeastTrainer;

  const setSourceData = useSetAtom(sourceDataAtom);

  const isEnabled =
    !(userIsLicenseeStaff || isLicenseeUser) ||
    !!activeLicenseeLocation?.number;

  const { isPending, isFetching, data } = useQuery({
    ...getUsersSummaryReportOptions({
      query: { locationNumbers: planLocations },
    }),
    enabled: isEnabled,
    select: results => processReportTeamMembers(results, planLocations),
  });

  const reportsTeamMembersIsFetching = isEnabled
    ? isPending || isFetching
    : isFetching;

  useScrollMemory({
    container: 'body',
    key: DATAVIEW_KEY,
    enabled: !reportsTeamMembersIsFetching,
  });

  useEffect(() => {
    setSourceData(TeamMemberVMFactory.fromUserPlanSummaryReport(data ?? []));
  }, [data, setSourceData]);

  // Clear Team Members Filters
  useEffect(() => {
    resetFilters();
  }, [resetFilters]);

  const hasUserLicenseeLocations = userLicenseeLocations.length > 0;
  const licenseeLocationsOptions: FilterCheckboxSectionProps = {
    title: t('Licensee.licenseeLocations'),
    onToggle: v => toggleCheckboxFilter('locations', v),
    labels: userLicenseeLocations.reduce<
      Record<string, { translationString: string; value: boolean }>
    >((acc, location) => {
      acc = {
        ...acc,
        [location.number ?? '']: {
          translationString: location.name ?? '',
          value: currentFilters.includes(location.number ?? ''),
        },
      };
      return acc;
    }, {}),
  };

  const hasMoreThanOneFranchiseeLocation = locations.length > 1;
  const genericLocationsOptions: FilterCheckboxSectionProps = {
    title: t('Generic.locations'),
    labels: locations.reduce(
      (acc, loc) => ({
        ...acc,
        [loc]: {
          translationString: loc,
          value: currentFilters.includes(loc),
        },
      }),
      {},
    ),
    onToggle: v => toggleCheckboxFilter('locations', v),
  };

  const searchHeaderOptions: FilterSearchBarProps = {
    onChange: e => setSearch(e.target.value, 'name'),
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.teamMembers.searchbarPlaceholder'),
    value: filters.search,
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      items.length
    } ${t('TrainingPlans.filtering.results')}`,
    onSortChange: setSort,
    sortOnly: userIsLicenseeStaff,
  };

  let filterContent: FilterLayoutProps | null;

  if (userIsLicenseeStaff) {
    // Brand Champion.
    filterContent = {
      before: (
        <>
          <LicenseeLocationTrigger className="block md:hidden" />
          <LicenseeLocationTrigger
            className="hidden md:block"
            wrapFilterSurface
          />
        </>
      ),
      search: <Filter.SearchBar {...searchHeaderOptions} />,
    };
  } else if (isOperatorConsultantUser) {
    // Operator Consultant
    filterContent = {
      before: (
        <>
          <ToggleButtons
            dataTestId="licensee-toggle-buttons"
            isLicenseeMode={isLicenseeMode}
            setIsLicenseeMode={setIsLicenseeMode}
          />
        </>
      ),
      children: isLicenseeMode
        ? hasUserLicenseeLocations && (
            <Filter.CheckboxSection {...licenseeLocationsOptions} />
          )
        : hasMoreThanOneFranchiseeLocation && (
            <Filter.CheckboxSection {...genericLocationsOptions} />
          ),
      search: <Filter.SearchBar {...searchHeaderOptions} />,
    };
  } else {
    // Default
    filterContent = {
      search: <Filter.SearchBar {...searchHeaderOptions} />,
      children: hasMoreThanOneFranchiseeLocation && (
        <Filter.CheckboxSection {...genericLocationsOptions} />
      ),
    };
  }

  const filterOptions = {
    ...filterContent,
    filterAndSortButtonOptions,
  };

  return (
    <div className="relative flex max-w-full grow flex-col">
      <LoadingOverlay isOpen={reportsTeamMembersIsFetching} />
      <div className="flex w-full flex-col">
        <div className="block text-center md:hidden">
          {!reportsTeamMembersIsFetching && <FilterMobile {...filterOptions} />}
        </div>
        {/* Top Row */}
        <div className="flex justify-end md:pl-[304px]">
          <SortFilterHeader
            label={t('TrainingPlans.filtering.sortBy')}
            onChange={setSort}
            onClear={() => resetAllCheckboxFilters()}
            options={sortOptions}
            showClear={false}
            showCompletedPlansOption={false}
            showMyCompletedPlansOnlySwitchValue={false}
            text={`${total} ${t('Reports.tabTeamMembers')}`}
            title={t('Reports.tabTeamMembers')}
            total={total}
            value={sortOptions.find(
              option =>
                option.key === sort?.key &&
                option.direction === sort?.direction,
            )}
          />
        </div>
        {/* Bottom Row */}
        <div className="mt-2.5 flex justify-between">
          {/* Left Side */}
          {!reportsTeamMembersIsFetching && (
            <FilterDesktop {...filterOptions} />
          )}
          {/* Right Side */}
          <div className="w-full">
            <div className="relative flex max-w-full grow flex-col overflow-hidden">
              <div className="relative flex max-w-full grow flex-col gap-4">
                {!!items.length &&
                  !reportsTeamMembersIsFetching &&
                  items.map((teamMember, idx) => (
                    <TeamMemberCard
                      completedStepCount={teamMember.plansCompleted ?? 0}
                      id={teamMember.id ?? ''}
                      isComplete={
                        teamMember.plansCompleted !== 0 &&
                        teamMember.plansCompleted === teamMember.plansAssigned
                      }
                      isReports={true}
                      key={`${idx} + ${teamMember.id ?? idx}`}
                      name={teamMember.name}
                      startDate={teamMember.startDate}
                      stepCount={teamMember.plansAssigned ?? 0}
                      simpleUserName={teamMember.simpleUserName}
                    />
                  ))}
                {!reportsTeamMembersIsFetching &&
                  userIsLicenseeStaff &&
                  !activeLicenseeLocation?.number && (
                    <NoMessage
                      message={t('Licensee.chooseALicenseeLocation')}
                    />
                  )}
                {(!!currentFilters.length || !!filters.search) && !total && (
                  <NoMessage
                    message={t('TrainingPlans.noTeamMembersResults')}
                  />
                )}
              </div>
              <LoadMorePaginator
                onClick={() => showMore()}
                showing={showing}
                showingText={t('TrainingPlans.showingXOfYTeamMembers', {
                  showing: showing,
                  total: total,
                })}
                total={total}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportsTeamMembersTabWithRoles = withRoles(
  ReportsTeamMembersTab,
  'reporting.teamMember',
);

export default ReportsTeamMembersTabWithRoles;
